'use client';

import { useIntl } from 'react-intl';
import { Card, CardContent, CardHeader } from '@packages/shared/src/ui/Card/Card';
import {
  getFragmentData,
  type FragmentType,
} from '@packages/gql/generated/shopping/fragment-masking';
import { RatingCardFragmentFragmentDoc } from '@packages/gql/generated/shopping/RatingCardFragmentFragmentDoc';
import { ratingCardMessages } from './RatingCard.messages';
import { RatingIconsWithReviewCount } from '../RatingIconsWithReviewCount/RatingIconsWithReviewCount';
import { RatingCardTags } from './RatingCardTags';
import { RatingCardResponse } from './RatingCardResponse';
import { RatingCardActionsMenu } from './RatingCardActionsMenu';

/* GraphQL */ `
  fragment RatingCardFragment on RatingReview {
    id
    created
    description
    rating
    reviewer {
      nickname
      location
    }
    reviewTags {
      proTags {
        id
        name
      }
      contraTags {
        id
        name
      }
    }
    title
    responses {
      title
      description
    }
  }
`;

export type RatingCardProps = {
  /**
   * data - the data needed to render the component
   */
  data: FragmentType<typeof RatingCardFragmentFragmentDoc>;
  /**
   * hideActionMenu - should the actions menu in the header be hidden
   */
  hideActionsMenu?: boolean;
};

/**
 * The RatingCard component displays a product rating written by a customer
 */
export const RatingCard = ({ data, hideActionsMenu, ...props }: RatingCardProps) => {
  const { id, reviewer, rating, created, title, description, responses, reviewTags } =
    getFragmentData(RatingCardFragmentFragmentDoc, data);
  const { formatMessage } = useIntl();

  return (
    <div {...props}>
      <Card>
        <CardHeader hasDivider>
          <div className="flex w-full flex-row flex-nowrap justify-between gap-1">
            <div className="mt-1">
              <RatingIconsWithReviewCount ratingValue={rating} ratingsCount={0} />
              <div className="text-size-body3 text-color-text-darktransparent mt-1">
                {formatMessage(ratingCardMessages.author, {
                  authorNickName: reviewer.nickname,
                  created,
                })}
              </div>
            </div>
            {!hideActionsMenu && <RatingCardActionsMenu ratingId={id} />}
          </div>
        </CardHeader>
        <CardContent className="py-2">
          <div className="mt-1 flex flex-col gap-1">
            <div className="text-size-body2">
              <div className="font-bold">{title}</div>
              <div className="whitespace-pre-wrap">{description}</div>
            </div>
            {reviewTags?.proTags?.length ? (
              <RatingCardTags
                data={reviewTags.proTags}
                label={formatMessage(ratingCardMessages.tagProLabel)}
              />
            ) : null}
            {reviewTags?.contraTags?.length ? (
              <RatingCardTags
                data={reviewTags.contraTags}
                label={formatMessage(ratingCardMessages.tagContraLabel)}
              />
            ) : null}
            {responses?.map((response, i) => (
              <RatingCardResponse
                data={response}
                label={formatMessage(ratingCardMessages.responseTitle)}
                // eslint-disable-next-line react/no-array-index-key
                key={i}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
